import { keepPreviousData, QueryClient, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import organizationService, { OrgApiParams } from '../services/organization.service';
import {
  CreateCompanyData,
  UpdateCompanyData,
  CreateTeamData,
  UpdateTeamData,
  CreateDepartmentData,
  UpdateDepartmentData,
} from '../types/organization.types';

const STALE_MS = 30_000;

export interface CompanyListParams {
  search: string;
  page: number;
}

export interface TeamListParams {
  search: string;
  company: string | 'ALL';
  activeOnly: boolean;
  page: number;
}

export interface DepartmentListParams {
  search: string;
  company: string | 'ALL';
  team: string | null;
  activeOnly: boolean;
  page: number;
}

export function companyApiParams(p: CompanyListParams): OrgApiParams {
  const params: OrgApiParams = { page: p.page };
  if (p.search) params.search = p.search;
  return params;
}

export function teamApiParams(p: TeamListParams): OrgApiParams {
  const params: OrgApiParams = { page: p.page };
  if (p.search) params.search = p.search;
  if (p.company !== 'ALL') params.company = p.company;
  if (p.activeOnly) params.is_active = true;
  return params;
}

export function departmentApiParams(p: DepartmentListParams): OrgApiParams {
  const params: OrgApiParams = { page: p.page };
  if (p.search) params.search = p.search;
  if (p.company !== 'ALL') params.team__company = p.company;
  if (p.team) params.team = p.team;
  if (p.activeOnly) params.is_active = true;
  return params;
}

// Page hooks

export function useCompaniesPage(p: CompanyListParams) {
  return useQuery({
    queryKey: ['companies-page', p],
    queryFn: () => organizationService.getCompaniesPage(companyApiParams(p)),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useTeamsPage(p: TeamListParams) {
  return useQuery({
    queryKey: ['teams-page', p],
    queryFn: () => organizationService.getTeamsPage(teamApiParams(p)),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useDepartmentsPage(p: DepartmentListParams) {
  return useQuery({
    queryKey: ['departments-page', p],
    queryFn: () => organizationService.getDepartmentsPage(departmentApiParams(p)),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

// Options hooks
//
// All three back the form-dialog selects, so they page-loop the full
// collection — a single page would silently cap the options list at the
// server page size. `enabled` lets the dialogs fetch only while open (and,
// where relevant, only while their level is the active one).

export function useCompanyOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['company-options'],
    queryFn: () => organizationService.getCompanies(),
    staleTime: STALE_MS,
    enabled,
  });
}

export function useTeamOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['team-options'],
    queryFn: () => organizationService.getAllTeams(),
    staleTime: STALE_MS,
    enabled,
  });
}

export function useDepartmentOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['department-options'],
    queryFn: () => organizationService.getAllDepartments(),
    staleTime: STALE_MS,
    enabled,
  });
}

// Detail hooks

export function useCompanyDetail(companyId: string | null) {
  return useQuery({
    queryKey: ['company-detail', companyId],
    queryFn: companyId == null ? skipToken : () => organizationService.getCompany(companyId),
    staleTime: STALE_MS,
  });
}

export function useTeamDetail(teamId: string | null) {
  return useQuery({
    queryKey: ['team-detail', teamId],
    queryFn: teamId == null ? skipToken : () => organizationService.getTeam(teamId),
    staleTime: STALE_MS,
  });
}

export function useDepartmentDetail(departmentId: string | null) {
  return useQuery({
    queryKey: ['department-detail', departmentId],
    queryFn: departmentId == null ? skipToken : () => organizationService.getDepartment(departmentId),
    staleTime: STALE_MS,
  });
}

// Children hooks

export function useCompanyTeams(companyId: string | null) {
  return useQuery({
    queryKey: ['company-teams', companyId],
    queryFn: companyId == null ? skipToken : () => organizationService.getCompanyTeams(companyId),
    staleTime: STALE_MS,
  });
}

export function useTeamDepartments(teamId: string | null) {
  return useQuery({
    queryKey: ['team-departments', teamId],
    queryFn: teamId == null ? skipToken : () => organizationService.getTeamDepartments(teamId),
    staleTime: STALE_MS,
  });
}

// Mutations
//
// Org entities denormalize across families — teams/departments carry parent
// names, parents carry child counts — and company/team deletes cascade
// server-side. Fine-grained invalidation would leave stale names, stale
// counts, and ghost rows for up to staleTime, so every mutation refreshes
// all org caches wholesale. These are admin-frequency writes over small
// datasets; the blanket refetch is cheap.
//
// Exported for the user-management hooks: role writes (and the role cascade
// of a user delete) change org user counts, so those mutations reuse this
// as their single invalidation fan-out.
export function invalidateOrgCaches(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['companies-page'] });
  queryClient.invalidateQueries({ queryKey: ['teams-page'] });
  queryClient.invalidateQueries({ queryKey: ['departments-page'] });
  queryClient.invalidateQueries({ queryKey: ['company-detail'] });
  queryClient.invalidateQueries({ queryKey: ['team-detail'] });
  queryClient.invalidateQueries({ queryKey: ['department-detail'] });
  queryClient.invalidateQueries({ queryKey: ['company-teams'] });
  queryClient.invalidateQueries({ queryKey: ['team-departments'] });
  queryClient.invalidateQueries({ queryKey: ['company-options'] });
  queryClient.invalidateQueries({ queryKey: ['team-options'] });
  queryClient.invalidateQueries({ queryKey: ['department-options'] });
  // The user-management caches denormalize org data too — user rows carry
  // role chips with org names, and the role dialog's tree mirrors the
  // hierarchy — so org renames/creates/deletes must refresh these families
  // as well.
  queryClient.invalidateQueries({ queryKey: ['users-page'] });
  queryClient.invalidateQueries({ queryKey: ['user-detail'] });
  queryClient.invalidateQueries({ queryKey: ['user-roles'] });
  queryClient.invalidateQueries({ queryKey: ['org-tree'] });
}

// Company mutations

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompanyData) => organizationService.createCompany(data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyData }) =>
      organizationService.updateCompany(id, data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationService.deleteCompany(id),
    onSuccess: (_result, id) => {
      // Drop the deleted company's caches outright — browser-back to its URL
      // must not re-render the detail or its children list from cache within
      // staleTime.
      queryClient.removeQueries({ queryKey: ['company-detail', id] });
      queryClient.removeQueries({ queryKey: ['company-teams', id] });
      invalidateOrgCaches(queryClient);
    },
  });
}

// Team mutations

export function useCreateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTeamData) => organizationService.createTeam(data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamData }) =>
      organizationService.updateTeam(id, data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationService.deleteTeam(id),
    onSuccess: (_result, id) => {
      // Drop the deleted team's caches outright — browser-back to its URL
      // must not re-render the detail or its children list from cache within
      // staleTime.
      queryClient.removeQueries({ queryKey: ['team-detail', id] });
      queryClient.removeQueries({ queryKey: ['team-departments', id] });
      invalidateOrgCaches(queryClient);
    },
  });
}

// Department mutations

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDepartmentData) => organizationService.createDepartment(data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDepartmentData }) =>
      organizationService.updateDepartment(id, data),
    onSuccess: () => invalidateOrgCaches(queryClient),
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationService.deleteDepartment(id),
    onSuccess: (_result, id) => {
      // Drop the cached detail outright — browser-back to the deleted
      // department's URL must not re-render it from cache within staleTime.
      queryClient.removeQueries({ queryKey: ['department-detail', id] });
      invalidateOrgCaches(queryClient);
    },
  });
}
